import { AMPLITUDE_QUERY_PARAMS } from "@logics/hooks/useAppDataReceiver";

export const URL_REGEX =
  /((?:(http|https|Http|Https):\/\/(?:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=]|(?:\%[a-fA-F0-9]{2})){1,64}(?:\:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=]|(?:\%[a-fA-F0-9]{2})){1,25})?\@)?)?((?:(?:[a-zA-Z0-9][a-zA-Z0-9\-\_]{0,64}\.)+(?:(?:aero|arpa|asia|a[cdefgilmnoqrstuwxz])|(?:biz|b[abdefghijmnorstvwyz])|(?:cat|com|coop|c[acdfghiklmnoruvxyz])|d[ejkmoz]|(?:edu|e[cegrstu])|f[ijkmor]|(?:gov|gle|g[abdefghilmnpqrstuwy])|h[kmnrtu]|(?:info|int|i[delmnoqrst])|(?:jobs|j[emop])|k[eghimnrwyz]|l[abcikrstuvy]|(?:mil|mobi|museum|m[acdghklmnopqrstuvwxyz])|(?:name|net|n[acefgilopruz])|(?:org|om)|(?:pro|p[aefghklmnrstwy])|qa|r[eouw]|site|s[abcdeghijklmnortuvyz]|(?:tel|travel|t[cdfghjklmnoprtvwz])|u[agkmsyz]|v[aceginu]|w[fs]|y[etu]|z[amw]|me))|(?:(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9])\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[0-9])))(?:\:\d{1,5})?)(\/(?:(?:[a-zA-Z0-9\;\/\?\:\@\&\=\#\~\-\.\+\!\*\'\(\)\,\_])|(?:\%[a-fA-F0-9]{2}))*)?(?:\b|$)/g;

const EXTERNAL_URL_PREFIX = "https://";

/**
 *
 * text
 * \{RegExpMatchArray\}
 *
 * - 해당 텍스트가 url링크인지 확인하고 url을 array로 return하는 함수
 * - a 태그의 id는 id-1, id-2, id-3 ... 순서로 증가
 * - a 태그의 class는 link-id로 통일 (해당하는 class를 찾아서 a 태그에 onclick 이벤트를 걸 수 있도록 하기 위함)
 */
export function extractUrlList(text: string) {
  return text.match(URL_REGEX);
}

export function linkify(inputText: string, id?: string) {
  let linkCount = 0; // NOTE: 같은 id로 여러 링크(a) 태그가 생성되는 것을 방지하기 위한 카운트

  return inputText.replace(URL_REGEX, (url) => {
    const urlWithPrefix = url.includes(EXTERNAL_URL_PREFIX)
      ? url
      : `${EXTERNAL_URL_PREFIX}${url}`;

    linkCount++;

    return `<a ${id ? `id="${id}-${linkCount}"` : ""} ${
      id ? `class="link-${id}"` : ""
    } href="${urlWithPrefix}" target="_blank">${url}</a>`;
  });
}

// ------------------------------------------------------------------------------------

/**
 * 주어진 URL에서 특정 쿼리 매개변수를 제거합니다.
 * @param url 수정할 URL
 * @param paramsToRemove 제거할 쿼리 매개변수 배열
 * @returns 지정된 쿼리 매개변수가 제거된 업데이트된 URL
 * @example
 * ```ts
 * const url = 'https://example.com/page?param1=value1&param2=value2&param3=value3';
 * const paramsToRemove = ['param1', 'param3'];
 * const updatedUrl = removeQueryParams(url, paramsToRemove);
 * console.log(updatedUrl); // https://example.com/page?param2=value2
 * ```
 */
export function removeQueryParams(
  url: string,
  paramsToRemove: string[],
): string {
  const parsedUrl = new URL(url);
  const searchParams = new URLSearchParams(parsedUrl.search);

  // 지정된 쿼리 매개변수를 제거합니다.
  paramsToRemove.forEach((param) => {
    searchParams.delete(param);
  });

  // 업데이트된 URL을 구성합니다.
  const updatedUrl = `${parsedUrl.origin}${parsedUrl.pathname}?${searchParams.toString()}`;

  return updatedUrl;
}

// ------------------------------------------------------------------------------------

/**
 * 주어진 URL에서 Amplitude의 특정 쿼리 매개변수를 제거하는 래퍼 함수입니다.
 * @param url 수정할 URL
 * @returns 지정된 쿼리 매개변수가 제거된 업데이트된 URL
 * @example
 * ```ts
 * const url = 'https://example.com/page?sessionId=1721694809116&userId=3&authProvider=google';
 * const updatedUrl = removeAmplitudeParams(url);
 * console.log(updatedUrl); // 결과: https://example.com/page
 * ```
 */
export function removeAmplitudeParams(url: string): string {
  return removeQueryParams(url, AMPLITUDE_QUERY_PARAMS);
}
